import { HtmlWithTheme } from '@core/components';
import { zIndexRange3dHtml } from '@core/constants';
import { vecFromLatLong } from '@core/logic';
import { useSMANumbers } from '@core/logic/hooks/useSMANumbers';
import { CameraFilled } from '@deepup/icons';
import { Badge, IconButton } from '@mui/material';
import type { Photo } from '@photos/types';
import { OrderNumber } from '@projects/types/orderNumbers';
import { Object3dNames } from '@viewer3D/types';
import React, { useMemo } from 'react';
import { gnssColor } from './helper';
import {
  clearPreviewPhotos,
  photosState,
  setIsHoveringIcon,
  setPreviewPosition,
  setSelectedPhoto,
  useSetPreviewPhotos,
  type Snapshot,
} from './state';

type PhotoIconProps = {
  photo?: Snapshot<Photo> | Photo;
  photos?: readonly Snapshot<Photo>[] | readonly Photo[];
  z: number;
  isSelected: boolean;
  projectId: string;
};

const PhotoIconComponent = ({ photo, photos = [], z, isSelected, projectId }: PhotoIconProps) => {
  const { setPreviewPhotos } = useSetPreviewPhotos();
  const { fetchSMANumbers } = useSMANumbers();

  // Normalize photos prop to always be an array (without mutating props)
  const normalizedPhotos = photo ? [photo] : photos;

  if (!normalizedPhotos || normalizedPhotos.length === 0) {
    console.warn('PhotoIcon received an empty photos array.');
    return null;
  }
  const isSinglePhoto = normalizedPhotos.length === 1;
  const position = vecFromLatLong(normalizedPhotos[0].lat, normalizedPhotos[0].long, z);

  // Check if the photo is newer than 72 hours - memoized to avoid recalculation on every render
  const isNewPhoto = useMemo(() => {
    return normalizedPhotos.some((p) => {
      const recordedDate = new Date(p.recordedAt);
      const hoursAgo = (Date.now() - recordedDate.getTime()) / (1000 * 60 * 60);
      return hoursAgo < 72;
    });
  }, [photo, photos]);

  const handleClick = async () => {
    const targetPhoto = normalizedPhotos[photosState.currentPhotoIndex];
    const signedUrl = photosState.previewPhotos.find((p) => p.id === targetPhoto.id)?.url;
    let smaNumbers: OrderNumber[] = [];
    try {
      smaNumbers = await fetchSMANumbers(projectId, targetPhoto.id, 'photos');
    } catch (error) {
      console.error('Failed to fetch SMA numbers:', error);
      smaNumbers = [];
    }
    const photoWithSMANumbers: Photo = {
      ...(targetPhoto as Photo),
      orderNumbers: smaNumbers,
    };

    setSelectedPhoto(signedUrl ? { ...photoWithSMANumbers, url: signedUrl } : photoWithSMANumbers);
  };

  const handleMouseEnter = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const position = { x: rect.right, y: rect.bottom };
    setIsHoveringIcon(true);
    setPreviewPosition(position);
    photosState.hoverPreviewPosition = position;
    await setPreviewPhotos(normalizedPhotos);
  };

  const handleMouseLeave = () => {
    setIsHoveringIcon(false);

    if (!photosState.selectedPhoto && photosState.previewPhotos.length === 0) {
      clearPreviewPhotos();
    }
  };

  return (
    <HtmlWithTheme
      position={position}
      center
      name={`${isSinglePhoto ? Object3dNames.Photo : Object3dNames.PhotoCluster}:${
        normalizedPhotos[0].id
      }`}
      zIndexRange={zIndexRange3dHtml}
    >
      <Badge
        overlap="circular"
        badgeContent={normalizedPhotos.length}
        color="secondary"
        sx={{
          filter: 'drop-shadow(0 0 3px #000000)',
          border: isSelected ? '2px solid #00ff00' : 'none',
          borderRadius: '50%',
        }}
        invisible={isSinglePhoto}
      >
        <Badge
          overlap="circular"
          variant="dot"
          sx={{
            '& .MuiBadge-dot': {
              backgroundColor: '#ff0000',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              border: '2px solid #000000',
            },
          }}
          invisible={!isNewPhoto}
        >
          <IconButton
            sx={{
              'div:has(> div > .MuiBadge-root > &:hover)': {
                /*
                  Brings the icon above all others on hover.
                  TODO: This is highly specific to the react-three/drei + MUI
                    element nesting and might not work in future drei/MUI versions!
                */
                zIndex: zIndexRange3dHtml[0] + 1 + ' !important',
              },
            }}
            size="small"
            color={
              isSinglePhoto
                ? (gnssColor[
                    normalizedPhotos[0]
                      .geoPositionSolution as import('@photos/types').GeoPositionSolution
                  ] ?? 'white')
                : undefined
            }
            title="Open Photo Details"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          >
            <CameraFilled fontSize={isSinglePhoto ? 24 : 32} />
          </IconButton>
        </Badge>
      </Badge>
    </HtmlWithTheme>
  );
};

export const PhotoIcon = React.memo(
  PhotoIconComponent,
  (prevProps: PhotoIconProps, nextProps: PhotoIconProps) => {
    const {
      photos: prevPhotos,
      photo: prevPhoto,
      isSelected: prevIsSelected,
      projectId: prevProjectId,
      z: prevZ,
    } = prevProps;
    const {
      photos: nextPhotos,
      photo: nextPhoto,
      isSelected: nextIsSelected,
      projectId: nextProjectId,
      z: nextZ,
    } = nextProps;

    // Check if primitive props changed
    if (prevIsSelected !== nextIsSelected || prevProjectId !== nextProjectId || prevZ !== nextZ) {
      return false;
    }

    // Handle single photo prop
    if (prevPhoto || nextPhoto) {
      // If one has photo and the other doesn't, re-render
      if (!prevPhoto || !nextPhoto) {
        return false;
      }
      // Compare single photo by reference (should be stable from parent)
      return prevPhoto === nextPhoto;
    }

    // Handle photos array prop
    // No photos in both cases
    if (!prevPhotos && !nextPhotos) {
      return true;
    }

    // One has photos, the other doesn't
    if (!prevPhotos || !nextPhotos) {
      return false;
    }

    // Both have photos - compare by reference equality and length
    // This assumes parent provides stable photo references
    if (prevPhotos.length !== nextPhotos.length) {
      return false;
    }

    // Check if all photos are the same by reference
    return prevPhotos.every((photo, index) => photo === nextPhotos[index]);
  },
);
